import React, { Fragment, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
	CalendarDaysIcon,
	ChevronDownIcon,
	CloudArrowUpIcon,
	RocketLaunchIcon,
} from '@heroicons/react/24/outline';

import { getEvents } from '../../api';
import useLocal from '../../hooks/useLocal';
import useModal from '../../hooks/useModal';
import useAsync from '../../hooks/useAsync';
import { getUserFullName } from '../helpers/functions';
import Avatar from '../Avatar';

const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const UserMenu = ({ navigation, avatarImage }) => {
	const navigate = useNavigate();

	const { get } = useLocal();
	const user = get('user');

	const { setOpen: openAddEventModal } = useModal('eventAdd');
	const { setOpen: openUploadDocumentsModal } = useModal('uploadDocuments');

	const eventsPayload = {
		status: ['ESTIMATED'],
		endDate: moment().add(30, 'd').toDate(),
	};

	const eventsExpiredScheduledPayload = {
		status: ['SCHEDULED'],
		endDate: moment().subtract(1, 'd').startOf('d').toDate(),
	};

	const {
		value: estimatedEvents,
		execute: refreshEvents,
		status,
	} = useAsync(getEvents, {
		defaultPayload: eventsPayload,
	});

	const { value: overdueScheduledEvents, execute: refreshExpiredEvents } =
		useAsync(getEvents, {
			defaultPayload: eventsExpiredScheduledPayload,
		});

	useEffect(() => {
		if (!open) {
			refreshEvents(eventsPayload, null, { silent: true });
			refreshExpiredEvents(eventsExpiredScheduledPayload, null, {
				silent: true,
			});
		}
	}, [open]);

	const combinedEvents = [
		...(estimatedEvents?.rows || []),
		...(overdueScheduledEvents?.rows || []),
	];

	return (
		<>
			<Menu as="div" className="relative ml-3">
				<div>
					<Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm">
						<RocketLaunchIcon className="w-6 h-6 text-gray-400" />
					</Menu.Button>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						{[
							{
								name: 'Schedule Service',
								onClick: () => openAddEventModal(),
								Icon: CalendarDaysIcon,
							},
							{
								name: 'Upload Document',
								onClick: () =>
									openUploadDocumentsModal({ moduleId: DOCUMENTS_MODULE_ID }),
								Icon: CloudArrowUpIcon,
							},
						].map((item) => (
							<Menu.Item key={item.name}>
								{({ active }) => (
									<a
										href={item.href}
										onClick={() => {
											if (item.path) {
												navigate(item.path);
											}
											if (typeof item.onClick === 'function') {
												item.onClick();
											}
										}}
										className={classNames(
											active ? 'bg-gray-100' : '',
											'group flex items-center px-4 py-2 text-xs cursor-pointer text-gray-500 hover:text-gray-600'
										)}
									>
										{item?.Icon ? (
											<item.Icon
												className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
												aria-hidden="true"
											/>
										) : null}
										{item.name}
									</a>
								)}
							</Menu.Item>
						))}
					</Menu.Items>
				</Transition>
			</Menu>
			<Menu as="div" className="relative ml-4">
				<div>
					<Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm">
						<span className="sr-only">Open user menu</span>
						{avatarImage ? (
							<img className="h-8 w-8 rounded-full" src={avatarImage} alt="" />
						) : (
							<>
								<Avatar size="xs" id={getUserFullName(user)} />
								<ChevronDownIcon className="w-4 h-4 ml-3" />
							</>
						)}
					</Menu.Button>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						{navigation.map((item) => (
							<Menu.Item key={item.name}>
								{({ active }) => (
									<a
										href={item.href}
										onClick={() => {
											if (item.path) {
												navigate(item.path);
											}
											if (typeof item.onClick === 'function') {
												item.onClick();
											}
										}}
										className={classNames(
											active ? 'bg-gray-100' : '',
											'block px-4 py-2 text-sm text-gray-700 cursor-pointer'
										)}
									>
										{item.name}
									</a>
								)}
							</Menu.Item>
						))}
					</Menu.Items>
				</Transition>
			</Menu>
		</>
	);
};

export default UserMenu;
